import { 
  MapPin, 
  Sun, 
  Battery, 
  DollarSign, 
  Calendar, 
  Zap, 
  Leaf,
  Building2,
  Factory,
  Home,
  TreePine,
  ArrowLeft,
  Edit3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProjectDetailsViewProps {
  project: {
    id: string;
    user_type: string;
    project_status: string;
    created_at: string;
    updated_at: string;
    location_data?: {
      address?: string;
      locations?: Array<{
        address?: string;
        latitude?: number;
        longitude?: number;
      }>;
    };
    solar_design?: {
      system_size?: number;
      panel_count?: number;
      yearly_production?: number;
      offset?: number;
    };
    bill_data?: {
      monthly_usage?: number;
      monthly_bill?: number;
      yearly_consumption?: number;
    };
    ai_offer?: any;
  };
}

const getUserTypeInfo = (userType: string) => {
  switch (userType) {
    case 'residential':
      return {
        label: 'Residential',
        icon: Home,
        color: 'bg-blue-50 text-blue-700',
        bgColor: 'bg-blue-50'
      };
    case 'commercial':
      return {
        label: 'Commercial',
        icon: Building2,
        color: 'bg-purple-50 text-purple-700',
        bgColor: 'bg-purple-50'
      };
    case 'industrial':
      return {
        label: 'Industrial',
        icon: Factory,
        color: 'bg-orange-50 text-orange-700',
        bgColor: 'bg-orange-50'
      };
    case 'agricultural':
      return {
        label: 'Agricultural',
        icon: TreePine,
        color: 'bg-green-50 text-green-700',
        bgColor: 'bg-green-50'
      };
    default:
      return {
        label: 'Unknown',
        icon: Home,
        color: 'bg-gray-50 text-gray-700',
        bgColor: 'bg-gray-50'
      };
  }
};

const getStatusInfo = (status: string) => {
  switch (status) {
    case 'completed':
      return {
        label: 'Completed',
        color: 'bg-green-100 text-green-800',
        dotColor: 'bg-green-500'
      };
    case 'in-progress':
      return {
        label: 'In Progress',
        color: 'bg-blue-100 text-blue-800',
        dotColor: 'bg-blue-500'
      };
    case 'pending':
    default:
      return {
        label: 'Pending',
        color: 'bg-yellow-100 text-yellow-800',
        dotColor: 'bg-yellow-500'
      };
  }
};

export function ProjectDetailsView({ project }: ProjectDetailsViewProps) {
  const navigate = useNavigate();
  const userTypeInfo = getUserTypeInfo(project.user_type);
  const statusInfo = getStatusInfo(project.project_status);
  const UserTypeIcon = userTypeInfo.icon;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getProjectLocation = () => {
    if (project.location_data?.address) {
      return project.location_data.address;
    }
    if (project.location_data?.locations?.[0]?.address) {
      return project.location_data.locations[0].address;
    }
    return 'Location not specified';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${userTypeInfo.bgColor}`}>
                  <UserTypeIcon className={`h-5 w-5 ${userTypeInfo.color}`} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {userTypeInfo.label} Solar Project
                  </h1>
                  <p className="text-sm text-gray-600">
                    Created {formatDate(project.created_at)}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate(`/project/${project.id}`)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Edit3 className="h-4 w-4" />
              Edit Project
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Project Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="font-medium text-gray-900">{getProjectLocation()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${statusInfo.dotColor}`}></div>
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {project.solar_design?.system_size && (
                    <div className="flex items-center gap-3">
                      <Zap className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">System Size</p>
                        <p className="font-medium text-gray-900">{project.solar_design.system_size} kW</p>
                      </div>
                    </div>
                  )}
                  
                  {project.bill_data?.monthly_bill && (
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Monthly Bill</p>
                        <p className="font-medium text-gray-900">${project.bill_data.monthly_bill.toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* System Details */}
            {project.solar_design && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">System Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {project.solar_design.system_size && (
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Sun className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">System Size</p>
                      <p className="text-xl font-bold text-gray-900">{project.solar_design.system_size} kW</p>
                    </div>
                  )}
                  
                  {project.solar_design.panel_count && (
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <Battery className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Panel Count</p>
                      <p className="text-xl font-bold text-gray-900">{project.solar_design.panel_count}</p>
                    </div>
                  )}
                  
                  {project.solar_design.yearly_production && (
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <Leaf className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Annual Production</p>
                      <p className="text-xl font-bold text-gray-900">
                        {Math.round(project.solar_design.yearly_production).toLocaleString()} kWh
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Energy Profile */}
            {project.bill_data && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Energy Profile</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {project.bill_data.monthly_usage && (
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <Zap className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Monthly Usage</p>
                      <p className="text-xl font-bold text-gray-900">
                        {project.bill_data.monthly_usage.toLocaleString()} kWh
                      </p>
                    </div>
                  )}
                  
                  {project.bill_data.monthly_bill && (
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <DollarSign className="h-8 w-8 text-red-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Monthly Bill</p>
                      <p className="text-xl font-bold text-gray-900">
                        ${project.bill_data.monthly_bill.toLocaleString()}
                      </p>
                    </div>
                  )}
                  
                  {project.bill_data.yearly_consumption && (
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <Battery className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Yearly Consumption</p>
                      <p className="text-xl font-bold text-gray-900">
                        {project.bill_data.yearly_consumption.toLocaleString()} kWh
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Information</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Project Type</p>
                  <p className="font-medium text-gray-900">{userTypeInfo.label}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="font-medium text-gray-900">{formatDate(project.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="font-medium text-gray-900">{formatDate(project.updated_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${statusInfo.dotColor}`}></div>
                    <span className={`text-sm font-medium px-2 py-1 rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/project/${project.id}`)}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit Project
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



